import React, { useState, useEffect } from 'react';
import { 
  Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Chip, TextField, InputAdornment,
  CircularProgress, Alert, TablePagination, IconButton, Tooltip
} from '@mui/material';
import {
  Search, Visibility, Add, Download, FilePresent, Delete,
  PictureAsPdf, InsertDriveFile, Image, Archive, Description,
  Refresh, FilterList, VideoCall
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { documentosApi, DocumentoConExpediente } from '../../services/api';

const DocumentosList: React.FC = () => {
  // Estados principales
  const [documentos, setDocumentos] = useState<DocumentoConExpediente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados de filtrado y paginación
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  const navigate = useNavigate();

  // Cargar documentos al inicializar
  useEffect(() => {
    loadDocumentos();
  }, []);

  const loadDocumentos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentosApi.getAllDocumentos(0, 1000); // Cargar todos
      setDocumentos(data);
    } catch (err: any) {
      setError(err.message || 'Error cargando documentos');
      console.error('Error cargando documentos:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar documentos
  const filteredDocumentos = documentos.filter(doc => {
    const matchesSearch = 
      doc.nombre_archivo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.expediente_codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.tipo_documento?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTipo = tipoFiltro === '' || doc.tipo_documento === tipoFiltro;
    
    return matchesSearch && matchesTipo;
  });

  // Obtener documentos paginados
  const paginatedDocumentos = filteredDocumentos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Funciones de utilidad
  const formatFileSize = (sizeKb?: number) => {
    if (!sizeKb) return 'N/A';
    if (sizeKb < 1024) return `${sizeKb} KB`;
    return `${(sizeKb / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (tipo: string) => {
    switch (tipo?.toLowerCase()) {
      case 'presupuesto':
      case 'proforma':
      case 'factura':
        return <PictureAsPdf color="error" />;
      case 'solicitud_costas':
        return <Description color="primary" />;
      case 'solicitud_videoconferencia':
        return <VideoCall color="primary" />;
      case 'informe':
        return <InsertDriveFile color="info" />;
      default:
        return <FilePresent color="action" />;
    }
  };

  const getTipoColor = (tipo: string): 'success' | 'info' | 'error' | 'default' | 'primary' | 'secondary' | 'warning' => {
    switch (tipo?.toLowerCase()) {
      case 'presupuesto': return 'info';
      case 'proforma': return 'warning';
      case 'factura': return 'success';
      case 'solicitud_costas': return 'primary';
      case 'solicitud_videoconferencia': return 'primary';
      case 'informe': return 'secondary';
      default: return 'default';
    }
  };

  const getEstadoColor = (estado: string): 'success' | 'info' | 'error' | 'default' | 'primary' | 'secondary' | 'warning' => {
    switch (estado?.toLowerCase()) {
      case 'finalizado': return 'success';
      case 'borrador': return 'warning';
      case 'pendiente': return 'info';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const handleDownload = async (documento: DocumentoConExpediente) => {
    try {
      const blob = await documentosApi.downloadDocumento(documento.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = documento.nombre_archivo || 'documento';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      setError(err.message || 'Error descargando documento');
    }
  };

  const handleViewExpediente = (expedienteId: string) => {
    navigate(`/expedientes/${expedienteId}`);
  };

  const handleDelete = async (documento: DocumentoConExpediente) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar "${documento.nombre_archivo}"?`)) {
      try {
        await documentosApi.deleteDocumento(documento.id);
        loadDocumentos(); // Recargar lista
      } catch (err: any) {
        setError(err.message || 'Error eliminando documento');
      }
    }
  };

  // Obtener tipos únicos para filtro
  const tiposUnicos = Array.from(new Set(documentos
    .map(doc => doc.tipo_documento)
    .filter(Boolean)
  )).sort();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography ml={2}>Cargando documentos...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          📁 Documentos ({filteredDocumentos.length})
        </Typography>
        <Box display="flex" gap={1}>
          <Button 
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadDocumentos}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => navigate('/expedientes')}
          >
            Nuevo Documento
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
          >
            Dashboard
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          fullWidth
          placeholder="Buscar por nombre, expediente, tipo o cliente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
        />
        <TextField
          select
          label="Tipo"
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          sx={{ minWidth: 200 }}
          SelectProps={{ native: true }}
        >
          <option value="">Todos los tipos</option>
          {tiposUnicos.map(tipo => (
            <option key={tipo} value={tipo}>
              {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
            </option>
          ))}
        </TextField>
      </Box>

      {/* Tabla de documentos */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Expediente</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Tamaño</TableCell>
              <TableCell>Fecha Creación</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedDocumentos.length > 0 ? (
              paginatedDocumentos.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getFileIcon(doc.tipo_documento)}
                      <Chip 
                        label={doc.tipo_documento} 
                        color={getTipoColor(doc.tipo_documento)}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {doc.nombre_archivo || doc.numero_documento || 'Sin nombre'}
                    </Typography>
                    {doc.numero_documento && (
                      <Typography variant="caption" color="textSecondary">
                        {doc.numero_documento}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {doc.expediente_codigo ? (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleViewExpediente(doc.expediente_id)}
                        sx={{ textTransform: 'none' }}
                      >
                        {doc.expediente_codigo}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Sin expediente
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {doc.cliente_nombre || 'Sin cliente'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={doc.estado || 'Sin estado'} 
                      color={getEstadoColor(doc.estado)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatFileSize(doc.tamaño_kb)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(doc.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Descargar">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDownload(doc)}
                          color="primary"
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver expediente">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewExpediente(doc.expediente_id)}
                          color="info"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(doc)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box py={4}>
                    <FilePresent sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      {searchTerm || tipoFiltro ? 'No se encontraron documentos' : 'No hay documentos'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {searchTerm || tipoFiltro 
                        ? 'Intenta cambiar los filtros de búsqueda'
                        : 'Los documentos generados aparecerán aquí'
                      }
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      {filteredDocumentos.length > 0 && (
        <TablePagination
          component="div"
          count={filteredDocumentos.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      )}
    </Box>
  );
};

export default DocumentosList;